// Phase 93 — single source of truth for an invoice's INR revenue
// contribution, mirroring backend `_invoice_revenue_inr` / `_is_received_status`
// exactly. Strict revenue recognition: ONLY a "received" (or legacy "paid")
// invoice contributes to Revenue anywhere in the app — Draft/Sent are ₹0.
// Master Registry override (Net Received ₹) wins the instant Finance sets it;
// until then it's Amount x FX Rate (FX Rate defaults to 1 for INR, 95 for
// any other currency, until Finance corrects it in the ledger). Used by
// the Dashboard, Finance Portal and Project P&L alike so every revenue
// number in the app always agrees.
export const DEFAULT_FX_RATE = 95;

// Feb 2026 — Multi-Currency Engine: same rate-resolution rule for Expenses
// as Revenue already uses (fx_rate wins when set; INR is always 1:1;
// anything else falls back to DEFAULT_FX_RATE until Finance corrects it).
// Purely a display-time derivation for reporting — never persisted, since
// it's fully recomputable from `amount` + `fx_rate` + `currency`.
export function expenseAmountINR(exp) {
  const amount = Number(exp?.amount) || 0;
  const currency = (exp?.currency || "INR").toUpperCase();
  const fx = exp?.fx_rate;
  const rate = fx !== null && fx !== undefined && fx !== "" ? Number(fx) : (currency === "INR" ? 1 : DEFAULT_FX_RATE);
  return amount * rate;
}

// Feb 2026 — Account Custom Sort: Finance's preferred priority order for
// every bank/card Account dropdown across the app (Revenue Ledger,
// Expenses Ledger, Petty Cash, FinanceEntryFields, Manage Recurring). Any
// account not on this list falls back to alphabetical, after the priority
// set.
export const ACCOUNT_PRIORITY_ORDER = ["Amex CC", "Amex Founder CC", "HDFC CC", "IDFC Opex Account", "HDFC Bank"];
export function sortSources(sources) {
  const rank = (name) => {
    const idx = ACCOUNT_PRIORITY_ORDER.findIndex((p) => p.toLowerCase() === (name || "").trim().toLowerCase());
    return idx === -1 ? ACCOUNT_PRIORITY_ORDER.length : idx;
  };
  return [...(sources || [])].sort((a, b) => {
    const ra = rank(a?.name), rb = rank(b?.name);
    if (ra !== rb) return ra - rb;
    return (a?.name || "").localeCompare(b?.name || "");
  });
}

// Feb 2026 — moved here (from pages/Finance.jsx) during the Finance.jsx
// split so every ledger component can import it without a circular
// dependency on the page itself. Full Indian-grouped rupee display, e.g.
// "₹ 1,23,456" — distinct from the compact fmtLakhs() used on chart axes.
export function fmt(n, c = "INR") { return `${c === "INR" ? "₹" : c} ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`; }

export function isReceivedStatus(status) {
  const s = (status || "").toLowerCase();
  return s === "received" || s === "paid";
}

export function invoiceEstimatedINR(inv) {
  const amount = Number(inv?.amount) || 0;
  const currency = (inv?.currency || "INR").toUpperCase();
  const fx = inv?.fx_rate;
  const rate = fx !== null && fx !== undefined && fx !== "" ? Number(fx) : (currency === "INR" ? 1 : DEFAULT_FX_RATE);
  return amount * rate;
}

export function invoiceRevenueINR(inv) {
  if (!isReceivedStatus(inv?.status)) return 0;
  const net = inv?.net_received_inr;
  if (net !== null && net !== undefined && net !== "") return Number(net) || 0;
  return invoiceEstimatedINR(inv);
}

// Raised-but-not-yet-received invoices (sent / overdue) at Amount x FX Rate —
// the "Outstanding" half of Revenue = Received + Outstanding.
export const OUTSTANDING_INVOICE_STATUSES = ["sent", "overdue"];
export function invoiceOutstandingINR(inv) {
  if (!OUTSTANDING_INVOICE_STATUSES.includes((inv?.status || "").toLowerCase())) return 0;
  return invoiceEstimatedINR(inv);
}

// Feb 2026 — Project Profitability Symmetry: mirrors backend
// `_is_countable_expense` exactly. Direct Costs in P&L/Profitability must
// be as conservative as the Revenue side's "only what's confirmed counts"
// rule — a row only counts once it's explicitly Paid, or it came from a
// workflow that IS an approval by construction (an approved Purchase
// Order's invoice-matched/closed mirror row, or a self-service Asset
// purchase auto-log). A plain manual ledger entry that hasn't been marked
// Paid yet does NOT count — it's logged spend, not yet a confirmed cost.
// Feb 2026 — Double-counting guard: categories that represent settling
// money already counted elsewhere (paying off a card whose spend was
// already logged as a real expense), never a NEW cost. Mirrors backend
// `_is_countable_expense()` / `_NON_COUNTABLE_CATEGORIES` exactly.
const NON_COUNTABLE_CATEGORIES = new Set(["internal transfer", "credit card payment", "credit card bill"]);

export function isCountableExpense(exp) {
  if (exp?.rejected) return false;
  // Internal Transfers and Credit Card Bill/Payment settlements are never a
  // real NEW cost (double-counting guards), so they never count toward Total
  // Studio Expense / Burn Rate / the Expense Trend graph. Oct 2026 — an
  // expense now counts as soon as it is LOGGED (payment status is a separate
  // visible flag, no longer a gate): only the two guards above exclude a row.
  if (NON_COUNTABLE_CATEGORIES.has((exp?.category || "").trim().toLowerCase())) return false;
  return true;
}

// Feb 2026 — Personnel Burn Normalization: "who spent this" (created_by_name
// / vendor) is free text, so trim + lowercase before ANY grouping or exact
// match so "Priya Sharma", "priya sharma " and "PRIYA SHARMA" are always
// treated as the exact same person everywhere (chart bars, ledger filter).
export function normalizePersonKey(name) {
  return (name || "").trim().toLowerCase();
}

// Feb 2026 — Personnel Burn Fix: the internal person an expense is
// attributed to — created_by_name (manual/asset entries) or
// requested_by_name (Purchase-Request-derived mirror rows), NEVER the
// `vendor` field. Returns null (not "Unattributed") when a row has no
// internal owner at all — e.g. a vendor self-uploaded PO invoice — so
// callers can cleanly EXCLUDE vendor/external rows instead of lumping
// them into a fake person bucket.
export function expensePersonName(exp) {
  const raw = (exp?.created_by_name || exp?.requested_by_name || "").trim();
  return raw || null;
}

// Phase 93 — Studio ERP invoice Service Type dropdown, single source of
// truth mirroring backend `INVOICE_SERVICE_TYPES` exactly.
export const INVOICE_SERVICE_TYPES = [
  "Concept Design", "MVP Prototyping", "DFM", "Launch Support",
  "3D Product Rendering", "GTM Services", "Technical Feasibility",
  "Sourcing Sprint", "Tooling Teardown", "Idea Feasibility", "Market Validation",
];

// Phase 93/140 — workflow statuses: Draft -> (manager Submit for Approval,
// PDF/Drive link required) -> Pending Approval -> (Founder Approve) ->
// Approved (Ready for Zoho) -> Sent -> Received.
export const INVOICE_STATUSES = ["draft", "pending_approval", "approved", "sent", "received", "overdue", "cancelled"];

// Human-facing label for every status above — single source of truth so the
// Ledger, Project page and Dashboard badges never drift in wording.
export const INVOICE_STATUS_LABELS = {
  draft: "Draft",
  pending_approval: "Pending Approval",
  approved: "Approved · Ready for Zoho",
  sent: "Sent",
  received: "Received",
  paid: "Received",
  overdue: "Overdue",
  cancelled: "Cancelled",
};

export function invoiceStatusLabel(status) {
  const s = (status || "draft").toLowerCase();
  return INVOICE_STATUS_LABELS[s] || s;
}

// Phase 94 — single INR formatter used everywhere (Finance page, all three
// ledgers, Dashboard, Expense Insights) so number formatting never drifts
// between components — Indian digit grouping (₹1,23,456), not Western.
export function fmtINR(n) {
  return `₹ ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

// Phase 94 — Month Selector: "All time" + "Year to Date" + the last 24
// calendar months, newest first. Every finance view (Dashboard Finance tab +
// the standalone Finance ledgers) shares this exact option list.
export function monthOptions(count = 24) {
  const now = new Date();
  const out = [{ value: "all", label: "All time" }, { value: "ytd", label: `Year to Date (${now.getFullYear()})` }];
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleString("en-US", { month: "long", year: "numeric" });
    out.push({ value, label });
  }
  return out;
}

export function currentMonthValue() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// Oct 2026 — single date-bucketing rule, mirrored in backend
// `_row_bucket_date`. A row is time-bucketed by, in order:
//   1. payment_received_date — REVENUE RULE 3: a received invoice is
//      recognised in the month its payment actually landed (required when an
//      invoice is marked Received; older received rows fall through).
//   2. accrual_date, then date — the row's own transaction date.
//   3. created_at — EXPENSE RULE 2: a row with no date at all falls back to
//      the date it was entered, so it never silently drops out of a month/
//      quarter. Returned as a 10-char YYYY-MM-DD so range compares are clean.
export function rowBucketDate(row) {
  const d = row?.payment_received_date || row?.accrual_date || row?.date || row?.created_at || "";
  return String(d).slice(0, 10);
}

// A row "belongs" to a selected month if its bucket date (see rowBucketDate)
// starts with that YYYY-MM — `month === "all"` always matches, `"ytd"`
// matches the current calendar year.
export function matchesMonth(row, month) {
  if (month === "all") return true;
  const d = rowBucketDate(row);
  if (month === "ytd") return d.slice(0, 4) === String(new Date().getFullYear());
  return d.slice(0, 7) === month;
}

// Server-side counterpart of `matchesMonth`: the {from,to} ISO window for a
// MonthSelector value ("all" → null = no filter, "ytd", or "YYYY-MM").
export function monthWindow(month) {
  if (!month || month === "all") return null;
  if (month === "ytd") { const y = new Date().getFullYear(); return { from: `${y}-01-01`, to: `${y}-12-31` }; }
  const [y, m] = month.split("-").map(Number);
  const last = new Date(y, m, 0).getDate();
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, "0")}` };
}

// Phase 122 — Finance Analytics Panel Global Filter Bar: Daily / Weekly /
// Monthly / Quarterly / Year-to-Date rolling windows (relative to today),
// shared by the big trend graph, the pie/personnel/burn widgets AND pushed
// down into the ledgers' own date-range filter so the whole Finance page
// is granularity-aware.
export const PERIOD_GRANULARITIES = [
  { id: "daily", label: "Daily" },
  { id: "weekly", label: "Weekly" },
  { id: "monthly", label: "Monthly" },
  { id: "quarterly", label: "Quarterly" },
  { id: "ytd", label: "Year to Date" },
  { id: "all", label: "All Time" },
];

// Phase 130 — `anchor.month`/`anchor.quarter` let the MoM/QoQ Time
// Navigator pick a SPECIFIC past (or current) period instead of always
// resolving to "now" — the single window used for StatCards + the ledger
// presetFilters below the master graph.
export function periodRange(granularity, anchor = {}) {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  if (granularity === "daily") return { from: iso(now), to: iso(now) };
  if (granularity === "weekly") {
    const start = new Date(now); start.setDate(now.getDate() - 6);
    return { from: iso(start), to: iso(now) };
  }
  if (granularity === "quarterly") {
    if (anchor.quarter) {
      const [qy, qn] = anchor.quarter.split("-Q").map(Number);
      const qStart = new Date(qy, (qn - 1) * 3, 1);
      const qEnd = new Date(qy, qn * 3, 0);
      const isCurrent = now >= qStart && now <= qEnd;
      return { from: iso(qStart), to: iso(isCurrent ? now : qEnd) };
    }
    const qStart = Math.floor(now.getMonth() / 3) * 3;
    return { from: iso(new Date(now.getFullYear(), qStart, 1)), to: iso(now) };
  }
  if (granularity === "ytd") return { from: iso(new Date(now.getFullYear(), 0, 1)), to: iso(now) };
  if (granularity === "all") return { from: "1970-01-01", to: iso(now) };
  // monthly
  if (anchor.month) {
    const [my, mm] = anchor.month.split("-").map(Number);
    const mStart = new Date(my, mm - 1, 1);
    const mEnd = new Date(my, mm, 0);
    const isCurrent = now.getFullYear() === my && now.getMonth() === mm - 1;
    return { from: iso(mStart), to: iso(isCurrent ? now : mEnd) };
  }
  return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(now) };
}

export function currentQuarterValue() {
  const now = new Date();
  return `${now.getFullYear()}-Q${Math.floor(now.getMonth() / 3) + 1}`;
}

// MoM Time Navigator — last 24 calendar months, newest first, "Sep 2026" style.
export function monthPickerOptions(count = 24) {
  const now = new Date();
  const out = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({ value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: d.toLocaleString("en-US", { month: "short", year: "numeric" }) });
  }
  return out;
}

// QoQ Time Navigator — last 12 quarters, newest first, "Q3 2026" style.
export function quarterPickerOptions(count = 12) {
  const now = new Date();
  const curQ = Math.floor(now.getMonth() / 3);
  const out = [];
  for (let i = 0; i < count; i++) {
    const idx = now.getFullYear() * 4 + curQ - i;
    const year = Math.floor(idx / 4);
    const q = (idx % 4) + 1;
    out.push({ value: `${year}-Q${q}`, label: `Q${q} ${year}` });
  }
  return out;
}

// ₹ Lakh/Crore compact formatter — Finance master graph Y-axis + tooltips.
// Feb 2026: auto-switches to Crores once a value crosses 1 Crore (100L =
// ₹1,00,00,000), e.g. ₹1.5L, ₹0.2L stay as Lakhs; ₹1,00,00,000+ becomes
// ₹1.0Cr, ₹100.0Cr — keeps large studio-wide totals readable.
export function fmtLakhs(v) {
  const n = Number(v || 0);
  if (Math.abs(n) >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  return `₹${(n / 100000).toFixed(1)}L`;
}

// A row falls in a period-range window if its bucket date (see
// rowBucketDate) is between `from` and `to`, inclusive.
export function matchesPeriod(row, { from, to }) {
  const d = rowBucketDate(row);
  if (!d) return false;
  return d >= from && d <= to;
}
